import { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useHistory } from "react-router-dom";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Flex,
  Span,
  Label,
  Table,
  Button,
  TableRow,
  useModal,
  NoResults,
  TableBody,
  TableCell,
  TableHead,
  TextInput,
  Pagination,
  FilterMenu,
  PageWrapper,
  StretchPanel,
  useFilterReducer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer,
} from "@sopranodesign/pulse-components";

import { EditLicense, CreateLicense, LicenseClientCard } from "../Components";
import { deleteLicense as deleteLicenseApi } from "../api";

dayjs.extend(relativeTime);

const Licenses = ({ match: { params }, location: { state: name } }) => {
  const history = useHistory();

  const [createLicense, setCreateLicense] = useState(false);

  const [editLicense, setEditLicense] = useState(false);

  const { showModal, hideModal } = useModal();

  const queryClient = useQueryClient();

  const pagination = usePaginationReducer({ id: "licenses" });
  const order = useOrderableTableReducer({ id: "licenses" });

  const filters = useFilterReducer({
    id: "licenses",
    initialFilters: { name: "" },
    options: { pagination },
  });

  const query = useQuery({
    queryKey: [
      `licenses/${params.projectId}`,
      { ...pagination.params, ...order.params, ...filters.form },
    ],
  });

  const handleExpiry = ({ status, period }) => {
    if (status && !period?.end) {
      return <Span>Never expires</Span>;
    }

    if (period?.end) {
      return (
        <Flex col>
          <Span>{dayjs(period.end).format("DD/MM/YYYY")}</Span>
          <Span light>{dayjs(period.end).from(dayjs())}</Span>
        </Flex>
      );
    }
  };

  const { mutate: deleteLicense } = useMutation({
    mutationFn: deleteLicenseApi,
  });

  const handleDestroy = (license) => {
    showModal({
      header: "Delete a license",
      body: `Are you sure you want to delete this license? If it's linked to a client, their bots will immediately stop working. Make sure you know what you're doing.`,
      handleConfirm: async () => {
        try {
          deleteLicense({
            project_id: params.projectId,
            license_id: license.license_id,
          });

          queryClient.invalidateQueries({
            queryKey: [`licenses/${params.projectId}`],
          });
        } finally {
          hideModal();
        }
      },
    });
  };

  const handleNameChange = (e) => {
    filters.updateFilters({ name: e.target.value });
  };

  return (
    <>
      <PageWrapper
        header="Licenses"
        subheader={`Manage licenses for ${name?.name ?? "your install"}`}
        actions={
          <Flex xSpace>
            <FilterMenu
              buttonProps={{ loading: query.isLoading }}
              {...filters.props}
            >
              <Flex style={{ width: "20rem" }} col>
                <Flex col fat mb>
                  <Label htmlFor="name">Account Name</Label>
                  <TextInput
                    id="name"
                    placeholder="Search by account name"
                    value={filters.filters.name}
                    onChange={handleNameChange}
                  />
                </Flex>
              </Flex>
            </FilterMenu>
            <Button
              icon="arrowNarrowLeft"
              variant="primary"
              onClick={() => history.goBack()}
            >
              Back
            </Button>
          </Flex>
        }
      >
        {createLicense && (
          <CreateLicense
            account_id={createLicense}
            project_id={params.projectId}
            handleCancel={() => setCreateLicense(false)}
          />
        )}

        {editLicense && (
          <EditLicense
            project_id={params.projectId}
            license={editLicense}
            handleCancel={() => setEditLicense(false)}
          />
        )}

        {query.isSuccess && query.data.data.length === 0 && (
          <NoResults
            text="No projects found"
            subtitle="Why not create a shiny new one"
          />
        )}

        {query.isError && (
          <NoResults
            text="There was a problem connecting to the project"
            subtitle="Double check the projects credentials or beg a developer for help"
          />
        )}

        {query.isSuccess && query.data.data.length > 0 && (
          <StretchPanel>
            <Table loading={query.isLoading} loadingColumns={4}>
              <TableHead>
                <OrderableTableRow
                  rows={[
                    { label: "Account information", sort: "account_name" },
                    { label: "Exact expiry", sort: null },
                    { label: "Actions", sort: null },
                  ]}
                  {...order.props}
                />
              </TableHead>
              {!query.isLoading && query.data?.data.length > 0 && (
                <TableBody>
                  {query.data.data.map((license, key) => (
                    <TableRow key={key}>
                      <TableCell>
                        <LicenseClientCard
                          client={{
                            logo: null,
                            name: license.account_name,
                            license,
                          }}
                        />
                      </TableCell>
                      <TableCell>{handleExpiry(license)}</TableCell>
                      <TableCell>
                        {!license.status && (
                          <Flex xSpace right>
                            <Button
                              icon="plus"
                              onClick={() =>
                                setCreateLicense(license.account_id)
                              }
                            >
                              Add license
                            </Button>
                          </Flex>
                        )}
                        {license.status && (
                          <Flex xSpace right>
                            <Button
                              icon="cog"
                              onClick={() => setEditLicense(license)}
                            >
                              Manage
                            </Button>
                            <Button
                              colour="danger"
                              icon="trash"
                              onClick={() => handleDestroy(license)}
                              variant="secondary"
                            >
                              Remove
                            </Button>
                          </Flex>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {query.showPagination && (
              <Pagination pagination={query.data.meta} {...pagination.props} />
            )}
          </StretchPanel>
        )}
      </PageWrapper>
    </>
  );
};

export default Licenses;
